import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RotateCcw, Search, AlertCircle } from "lucide-react";
import { useStock } from "@/context/StockContext";
import { Sale } from "@/types/stock";
import { toast } from "sonner";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

export const SaleReturnDialog: React.FC = () => {
  const { t } = useTranslation();
  const { sales, returnSale } = useStock();
  const [open, setOpen] = useState(false);
  const [saleNumber, setSaleNumber] = useState("");
  const [foundSale, setFoundSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!saleNumber.trim()) return;

    const sale = sales.find((s) => s.saleNumber === saleNumber.trim());
    if (sale) {
      setFoundSale(sale);
    } else {
      setFoundSale(null);
      toast.error(t("stock.saleNotFound"));
    }
  };

  const handleReturn = async () => {
    if (!foundSale) return;

    if (!confirm(t("stock.confirmReturn"))) {
      return;
    }

    setLoading(true);
    try {
      await returnSale(foundSale.id);
      setOpen(false);
      setFoundSale(null);
      setSaleNumber("");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSaleNumber("");
    setFoundSale(null);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
        setOpen(val);
        if (!val) resetForm();
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <RotateCcw className="h-4 w-4" />
          {t("stock.returnSale")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("stock.processReturn")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="saleNumber">{t("stock.saleNumber")}</Label>
              <Input
                id="saleNumber"
                value={saleNumber}
                onChange={(e) => setSaleNumber(e.target.value)}
                placeholder="Ex: SALE-123456"
              />
            </div>
            <Button type="submit" className="mt-auto">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          {foundSale && (
            <div className="border rounded-md p-4 bg-slate-50 dark:bg-slate-900 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{foundSale.saleNumber}</h3>
                  <p className="text-sm text-gray-500">
                    {format(new Date(foundSale.createdAt), "PPP p")}
                  </p>
                </div>
                <Badge variant={foundSale.status === "returned" ? "destructive" : "default"}>
                  {foundSale.status}
                </Badge>
              </div>

              <div className="border-t pt-2 mt-2">
                <h4 className="text-sm font-medium mb-2">{t("stock.items")}:</h4>
                <ul className="text-sm space-y-1">
                  {foundSale.items.map((item) => (
                    <li key={item.id} className="flex justify-between">
                      <span>
                        {item.quantity}x {item.product?.name || "Unknown Product"}
                      </span>
                      <span>{item.totalPrice.toFixed(3)} TND</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                <span>Total</span>
                <span>{foundSale.totalAmount.toFixed(3)} TND</span>
              </div>
              
              {foundSale.status === "returned" && (
                  <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 p-2 rounded">
                      <AlertCircle className="h-4 w-4" />
                      {t("stock.saleAlreadyReturned")}
                  </div>
              )}

              <Button 
                onClick={handleReturn} 
                className="w-full mt-4" 
                variant="destructive"
                disabled={loading || foundSale.status === "returned"}
              >
                {loading ? "Processing..." : t("stock.confirmReturn")}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
